<script setup lang="ts">
  import { computed, useId, useSlots } from 'vue';
  import { Badge } from '../Badge/';
  import { Attribute } from '../Attribute/';
  import { FeedbackMessage } from '../FeedbackMessage/';
  import { Button } from '../Button/';
  import type {
    InformativeCardActionProps,
    InformativeCardProps,
  } from './InformativeCard.types';
  import {
    InformativeCardBackground,
    InformativeCardLeadingSize,
  } from './InformativeCard.types';
  import {
    actionClass,
    backgroundClasses,
    cardClass,
    dataRowClass,
    descriptionClass,
    headerActionsClass,
    headerClass,
    headerTitleGroupClass,
    leadingClass,
    leadingImageClass,
    leadingPreviewImageClass,
    leadingSizeClasses,
    paddedSectionClass,
    sectionClass,
    stripeClass,
    titleClass,
    titleRowClass,
  } from './InformativeCard.css';
  import LegacyAttributeList from '../LegacyAttributeList/LegacyAttributeList.vue';

  const props = withDefaults(defineProps<InformativeCardProps>(), {
    highlighted: false,
    padded: false,
    backgroundColor: InformativeCardBackground.BASE,
    leadingSize: InformativeCardLeadingSize.AUTO,
  });

  const slots = useSlots();
  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-informative-card-${uid}`,
  );

  const emit = defineEmits<{
    action: [index: number];
    headerAction: [index: number];
    feedbackLinkClick: [];
  }>();

  const cardClasses = computed(() => [
    cardClass,
    backgroundClasses[props.backgroundColor],
  ]);

  const hasHeaderActions = computed(
    () => !!props.headerActions && props.headerActions.length > 0,
  );

  const hasTitleEnd = computed(() => !!slots['title-end']);

  const hasHeader = computed(
    () =>
      !!props.badge ||
      !!props.title ||
      !!props.description ||
      hasTitleEnd.value ||
      hasHeaderActions.value,
  );

  const hasAttributesFromProps = computed(
    () => !!props.attributes && props.attributes.length > 0,
  );

  const hasAttributesSlot = computed(() => !!slots.attributes);

  const showAttributes = computed(
    () => hasAttributesFromProps.value || hasAttributesSlot.value,
  );

  const resolvedActions = computed<InformativeCardActionProps[]>(() => {
    if (props.actions?.length) return props.actions;
    return props.action ? [props.action] : [];
  });

  const hasActions = computed(() => resolvedActions.value.length > 0);

  const toButtonProps = ({
    label,
    ...buttonProps
  }: InformativeCardActionProps) => buttonProps;
</script>

<template>
  <div :class="cardClasses" :data-testid="resolvedTestId">
    <div
      v-if="highlighted"
      :class="stripeClass"
      :data-testid="`${resolvedTestId}-stripe`"
    />

    <div
      v-if="leadingSrc"
      :class="[leadingClass, leadingSizeClasses[leadingSize]]"
      :data-testid="`${resolvedTestId}-leading`"
    >
      <img
        :class="
          leadingSize === InformativeCardLeadingSize.PREVIEW
            ? leadingPreviewImageClass
            : leadingImageClass
        "
        :src="leadingSrc"
        :alt="leadingAlt ?? ''"
        :aria-hidden="!leadingAlt ? 'true' : undefined"
        loading="lazy"
      >
    </div>

    <div :class="[sectionClass, { [paddedSectionClass]: padded }]">
      <div
        v-if="hasHeader"
        :class="headerClass"
        :data-testid="`${resolvedTestId}-header`"
      >
        <Badge v-if="badge" v-bind="badge" />

        <div
          v-if="title || description || hasTitleEnd"
          :class="headerTitleGroupClass"
        >
          <div v-if="title || hasTitleEnd" :class="titleRowClass">
            <p v-if="title" :class="titleClass" role="heading" aria-level="3">
              {{ title }}
            </p>
            <slot name="title-end" />
          </div>
          <span v-if="description" :class="descriptionClass">{{
            description
          }}</span>
        </div>

        <div
          v-if="hasHeaderActions"
          :class="headerActionsClass"
          :data-testid="`${resolvedTestId}-header-actions`"
        >
          <Button
            v-for="(headerAction, index) in headerActions"
            :key="index"
            v-bind="toButtonProps(headerAction)"
            @click="emit('headerAction', index)"
          >
            <template v-if="headerAction.label">
              {{ headerAction.label }}
            </template>
          </Button>
        </div>
      </div>

      <div
        v-if="showAttributes || hasActions"
        :class="dataRowClass"
        :data-testid="`${resolvedTestId}-data`"
      >
        <LegacyAttributeList v-if="showAttributes" :expand="expandAttributes">
          <slot name="attributes">
            <Attribute
              v-for="(attr, index) in attributes"
              :key="index"
              v-bind="attr"
            />
          </slot>
        </LegacyAttributeList>

        <div
          v-if="hasActions"
          :class="actionClass"
          :data-testid="`${resolvedTestId}-action`"
        >
          <Button
            v-for="(cardAction, index) in resolvedActions"
            :key="index"
            v-bind="toButtonProps(cardAction)"
            @click="emit('action', index)"
          >
            <template v-if="cardAction.label">{{ cardAction.label }}</template>
          </Button>
        </div>
      </div>

      <FeedbackMessage
        v-if="feedbackMessage"
        v-bind="feedbackMessage"
        mode="inline"
        @link-click="emit('feedbackLinkClick')"
      />
    </div>
  </div>
</template>
